import React, { useState } from 'react';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Alert,
  AlertColor,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Check, Clear, ContentCopy } from '@mui/icons-material';
import { ContentInput, Title, CopyBox, CopyIcon, CopyText, AnimatedIcon, ConfirmBox } from './styles';

interface Props {
  id: string;
  type: string;
  version: string;
  onCreate: (data: CreateActivationCodeProps) => void;
}

interface CreateActivationCodeProps {
  name: string;
  isActive: boolean;
  codeExpiresAt: Date | null;
}

const CreateActivationCode: React.FC<Props> = (props) => {
  const [data, setData] = useState<CreateActivationCodeProps>({
    name: "",
    isActive: true,
    codeExpiresAt: null,
  });

  const [showStatus, setShowStatus] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusSeverity, setStatusSeverity] = useState<AlertColor>("error");

  const [confirmedCode, setConfirmedCode] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [copied, setCopied] = useState(false);
  const handleCopy = (textToCopy: string) => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleCloseStatus = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShowStatus(false);
  };

  const handleCreate = () => {
    if (!data.name) {
      setShowStatus(true);
      setStatusMsg("Please enter a name for the activation code.");
      setStatusSeverity("error");
      return;
    }
    onCreate(data);
  };

  const onCreate = (params: {
    name: string;
    isActive: boolean;
    codeExpiresAt: Date | null;
  }) => {
    const expirationDate = params.codeExpiresAt ? params.codeExpiresAt.getTime() : null;

    fetch("/api/v1/software/type/version/activationCode/add", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        software: props.id,
        type: props.type,
        version: props.version,
        name: params.name,
        isActive: params.isActive,
        codeExpiresAt: expirationDate,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === true) {
          setConfirmedCode(data.code);
          // props.onCreate(data);
        }
      }).catch((err) => console.log("Error while creating activation code:", err));
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          maxWidth: '640px',
          margin: '0 auto',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        { !confirmedCode &&
          <Title>
            Create Activation Code
          </Title>
        }

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {!confirmedCode ?
            <>
              <ContentInput
                label="Code Name"
                variant="standard"
                value={data.name}
                onChange={(e) =>
                  setData({ ...data, name: e.target.value })
                }
                fullWidth
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiration Date"
                  disablePast
                  value={data.codeExpiresAt}
                  onChange={(newValue) =>
                    setData({ ...data, codeExpiresAt: newValue })
                  }
                  renderInput={(params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                      />
                      {data.codeExpiresAt && (
                        <IconButton
                          onClick={() => setData({ ...data, codeExpiresAt: null })}
                          sx={{ color: 'red' }}
                        >
                          <Clear />
                        </IconButton>
                      )}
                    </Box>
                  )}
                />
              </LocalizationProvider>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={data.isActive}
                    onChange={(e) =>
                      setData({ ...data, isActive: e.target.checked })
                    }
                  />
                }
                label="Active"
                sx={{
                  color: "#00000099",
                  fontSize: "1rem",
                }}
              />

              <Button
                variant="contained"
                onClick={handleCreate}
                sx={{
                  mt: 2,
                  backgroundColor: "#3f51b5",
                  color: "#fff",
                  alignSelf: isMobile ? 'stretch' : 'flex-start',
                }}
              >
                Create
              </Button>
            </>
            :
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 5,
                  padding: 2,
                }}
              >
                <AnimatedIcon 
                  sx={{
                    fontSize: 150,
                  }}
                />
                <ConfirmBox>
                  Activation Code Created Successfully!
                </ConfirmBox>
                <CopyBox>
                  <CopyText>
                    {confirmedCode}
                  </CopyText>
                  <CopyIcon onClick={() => handleCopy(confirmedCode)}>
                    {copied ? <Check sx={{ fontSize: 18 }} /> : <ContentCopy sx={{ fontSize: 18 }} />}
                  </CopyIcon>
                </CopyBox>
              </Box>
            </>
          }
        </Box>
      </Box>

      <Snackbar
        open={showStatus}
        autoHideDuration={6000}
        onClose={handleCloseStatus}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseStatus}
          severity={statusSeverity}
          sx={{ width: "100%" }}
        >
          {statusMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateActivationCode;
